import { Button, Textarea, TextInput } from '@mantine/core';
import { FileWithPath } from '@mantine/dropzone';
import { useForm, zodResolver } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Api } from '@/shared/api';
import { RouteList } from '@/shared/constants';
import { useDirtyForm } from '@/shared/hooks/use-dirty-form';
import {
  useCurrentProject,
  useProjectPath,
} from '@/shared/projects/project-path';
import { SelectFileInput } from '@/widgets/select-file-input';

import {
  useUpdatePublisherMutation,
  useUploadPublisherPreviewMutation,
} from '../model/edit-publisher-page.cases';
import {
  EDIT_PUBLISHER_SCHEMA,
  KITABA_EDIT_PUBLISHER_SCHEMA,
} from '../model/edit-publisher-page.schemas';
import styles from './edit-publisher-page.module.scss';

export const EditPublisherPage = () => {
  const project = useCurrentProject();
  const form = useForm({
    initialValues: {
      title: '',
      description: '',
      url: '',
      previewFile: null as FileWithPath | null,
      previewUrl: '',
    },
    validate: zodResolver(
      project === 'kitaba'
        ? KITABA_EDIT_PUBLISHER_SCHEMA
        : EDIT_PUBLISHER_SCHEMA,
    ),
  });
  const params = useParams();
  const withProject = useProjectPath();
  useDirtyForm(form.isDirty());

  const getPublisherQuery = useQuery({
    queryKey: [project, 'publisher', params.publisherId],
    queryFn: () =>
      Api.publishers.getPublisher(project, Number(params.publisherId)),
    enabled: !!params.publisherId,
  });
  const updatePublisherMutation = useUpdatePublisherMutation();
  const uploadPublisherPreviewMutation = useUploadPublisherPreviewMutation();
  const navigate = useNavigate();

  async function updatePublisher(data: {
    title: string;
    description: string;
    url: string;
    previewFile: FileWithPath | null;
  }) {
    let imageUrl: string | null = null;

    if (data.previewFile) {
      imageUrl = await uploadPublisherPreviewMutation
        .mutateAsync(data.previewFile)
        .catch((reason) => {
          notifications.show({
            message: 'Не удалось загрузить изображение',
            color: 'red',
          });

          throw reason;
        });
    }
    await updatePublisherMutation
      .mutateAsync({
        id: Number(params.publisherId),
        title: data.title,
        description: data.description,
        url: data.url,
        imgUrl: imageUrl,
      })
      .then(() => {
        notifications.show({
          message: 'Издательство обновлено',
          color: 'green',
        });
        return navigate(
          withProject(RouteList.PUBLISHER_DETAILS(String(params.publisherId))),
          {
            replace: true,
          },
        );
      })
      .catch(() => {
        notifications.show({
          message: 'Не удалось обновить издательство',
          color: 'red',
        });
      });
  }

  useEffect(() => {
    if (getPublisherQuery.data) {
      form.setValues({
        title: getPublisherQuery.data.title,
        description: getPublisherQuery.data.description || '',
        url: getPublisherQuery.data.url || '',
        previewUrl: getPublisherQuery.data.imgUrl || '',
      });
      form.resetDirty();
    }
  }, [getPublisherQuery.data]);

  return (
    <form
      className={styles.container}
      onSubmit={form.onSubmit(updatePublisher)}
    >
      <p className={styles.title}>Редактирование издательства</p>
      <TextInput
        placeholder="Название издательства"
        label="Название"
        className={styles.input}
        {...form.getInputProps('title')}
      />
      <Textarea
        placeholder="Введите текст"
        label="Описание"
        className={styles.input}
        {...form.getInputProps('description')}
      />
      <TextInput
        placeholder="Вставьте ссылку"
        label="Ссылка"
        className={styles.input}
        {...form.getInputProps('url')}
      />
      <SelectFileInput
        onDrop={(files) => {
          form.setFieldValue('previewUrl', URL.createObjectURL(files[0]));
          form.setFieldValue('previewFile', files[0]);
        }}
        label="Обложка"
        description={`Допустимые форматы файлов: png
          Размер файла не должен превышать 400 кб`}
        className={styles.previewZone}
        {...form.getInputProps('previewUrl')}
      />
      <img
        className={styles.preview}
        src={form.values.previewUrl}
        alt="preview"
      />
      <div className={styles.actions}>
        <Button type="submit">Сохранить</Button>
        <Button variant="light" onClick={() => history.back()}>
          Отменить
        </Button>
      </div>
    </form>
  );
};
